import { Button, Dialog, DialogActions, DialogContent, DialogTitle, CircularProgress, Box, Tooltip } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import CheckIcon from '@mui/icons-material/Check';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ImageIcon from '@mui/icons-material/Image';
import MyRichTextEditor from "../../../components/MyRichTextEditor";
import { uploadEmailFile, generateUniqueImageKey, fetchEmailHtml } from "../../../api/emailConfigApi";
import { EMAIL_TYPES } from ".";


interface TemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (htmlContent: string, typeId: number) => Promise<void>;
  ruc: string;
  emailType: EMAIL_TYPES;
  typeId: number;
  initialHtml?: string;
}

interface PendingImage {
  file: File;
  localUrl: string;
}

const EMAIL_VARIABLES = [
  { code: "{{Nombre}}", description: "Nombre completo del colaborador" },
  { code: "{{Fecha}}", description: "Fecha del onomástico (DD/MM)" },
  { code: "{{Empresa}}", description: "Nombre de la organización" }
];

const TemplateEditorModal: React.FC<TemplateModalProps> = ({ 
  open, onClose, onSave, ruc, emailType, typeId 
}) => {
  const [htmlContent, setHtmlContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingHtml, setLoadingHtml] = useState<boolean>(false);
  const [pendingImages, setPendingImages] = useState<PendingImage[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    
    const loadHtml = async () => {
      setLoadingHtml(true);
      try {
        const htmlFromS3 = await fetchEmailHtml(ruc, emailType);
        if (htmlFromS3) {
          setHtmlContent(htmlFromS3);
        } else {
          setHtmlContent("<p>¡Hola! Te deseamos un excelente cumpleaños...</p>");
        }
        setPendingImages([]);
      } catch (error) {
        console.error("Error al cargar HTML desde S3:", error);
        setHtmlContent("<p>¡Hola! Te deseamos un excelente cumpleaños...</p>");
      } finally {
        setLoadingHtml(false);
      }
    };

    loadHtml();
  }, [open, ruc, emailType]);

  const handleRegisterLocalImage = (file: File, localUrl: string) => {
    setPendingImages(prev => [...prev, { file, localUrl }]);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      let finalHtml = htmlContent;

      for (const item of pendingImages) {
        if (finalHtml.includes(item.localUrl)) {
          const uniqueKey = generateUniqueImageKey(item.file.name);
          const publicUrl = await uploadEmailFile(ruc, emailType, uniqueKey, item.file);
          if (publicUrl) {
            finalHtml = finalHtml.replaceAll(item.localUrl, publicUrl);
          }
        }
      }

      const htmlBlob = new Blob([finalHtml], { type: 'text/html;charset=utf-8' });
      const htmlFile = new File([htmlBlob], 'body.html', { type: 'text/html' });
      await uploadEmailFile(ruc, emailType, 'body.html', htmlFile);

      await onSave(finalHtml, typeId);
      onClose();
    } catch (error) {
      console.error("Error guardando la plantilla", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadHtmlFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      if (content) {
        setHtmlContent(content);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    };
    reader.readAsText(file, 'UTF-8');
  };

  const handleLoadImages = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    let updatedHtml = htmlContent;

    Array.from(files).forEach((file) => {
      const localUrl = URL.createObjectURL(file);
      setPendingImages(prev => [...prev, { file, localUrl }]);

      const fileName = file.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`src="([^"]*${fileName})"`, 'gi');
      updatedHtml = updatedHtml.replace(regex, `src="${localUrl}"`);
    });

    setHtmlContent(updatedHtml);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const handleViewHtml = () => {
    if (!htmlContent) return;
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head><meta charset="UTF-8"><title>Vista previa</title></head>
          <body>${htmlContent}</body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: '700' }}>
        Diseñador Visual de Plantilla de Correo
      </DialogTitle>
      <DialogContent dividers>
        {loadingHtml ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <MyRichTextEditor 
            value={htmlContent} 
            onChange={setHtmlContent} 
            variables={EMAIL_VARIABLES}
            onLocalImageAdded={handleRegisterLocalImage}
          />
        )}
      </DialogContent>
      <DialogActions style={{ padding: "1rem", justifyContent: "space-between" }}>
        <Button onClick={onClose} color="primary" disabled={loading || loadingHtml}>
          Cancelar
        </Button>
        <Box display="flex" gap={1}>
          <Tooltip title="Cargar archivo HTML">
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => fileInputRef.current?.click()} disabled={loading || loadingHtml}>
              Cargar HTML
            </Button>
          </Tooltip>
          <input type="file" accept=".html,.htm" ref={fileInputRef} style={{ display: 'none' }} onChange={handleLoadHtmlFile} />

          <Tooltip title="Cargar imágenes (reemplazará las rutas que coincidan con el nombre del archivo)">
            <Button variant="outlined" startIcon={<ImageIcon />} onClick={() => imageInputRef.current?.click()} disabled={loading || loadingHtml}>
              Cargar imágenes
            </Button>
          </Tooltip>
          <input type="file" accept="image/*" multiple ref={imageInputRef} style={{ display: 'none' }} onChange={handleLoadImages} />

          <Tooltip title="Ver HTML en nueva pestaña">
            <Button variant="outlined" startIcon={<VisibilityIcon />} onClick={handleViewHtml} disabled={loading || loadingHtml || !htmlContent}>
              Ver HTML
            </Button>
          </Tooltip>

          <Button onClick={handleSave} color="primary" variant="contained" disabled={loading || loadingHtml} startIcon={<CheckIcon />}>
            Guardar Cambios
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateEditorModal;